import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useMemo, useState} from "react";
import MakeItLiveCta from "../components/MakeItLiveCta.tsx";
import {GeneratorProgress, GeneratorTopCta, useStagedGenerate} from "../components/generatorReveal.tsx";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/round-robin-tournament-generator')({
    component: RouteComponent,
})

type Round = {label: string; matches: string[]};

/**
 * Single round robin via the circle method: pin the first entrant, rotate the rest.
 * An odd entrant count gets a "BYE" so pairings stay even; BYE matches are dropped.
 */
function buildRoundRobin(entrants: string[]): Round[] {
    let players = [...entrants];
    if (players.length % 2 !== 0) {
        players.push("BYE");
    }

    const rounds: Round[] = [];
    const totalRounds = players.length - 1;
    const matchesPerRound = players.length / 2;

    for (let round = 0; round < totalRounds; round++) {
        const matches: string[] = [];
        for (let m = 0; m < matchesPerRound; m++) {
            const home = players[m];
            const away = players[players.length - 1 - m];
            if (home !== "BYE" && away !== "BYE") {
                matches.push(`${home} vs ${away}`);
            }
        }
        rounds.push({label: `Round ${round + 1}`, matches});

        // Rotate: keep players[0] fixed, move the last entrant to the front of the rest.
        players = [
            players[0],
            players[players.length - 1],
            ...players.slice(1, players.length - 1),
        ];
    }

    return rounds;
}

const STEPS = [
    {title: "Add Players or Teams", description: "Enter every player or team taking part, one per line."},
    {title: "Generate Fixtures", description: "ScorePal builds each round and every matchup automatically."},
    {title: "Run Your Event", description: "Manage live scores, standings and rankings in one place."},
];

const MATCH_TABLE = [
    {n: 4, matches: 6},
    {n: 6, matches: 15},
    {n: 8, matches: 28},
    {n: 10, matches: 45},
    {n: 12, matches: 66},
    {n: 16, matches: 120},
];

const WHY_ITEMS = [
    {title: "Fairer Results", description: "Every participant plays everyone else, so final standings reflect real performance."},
    {title: "More Playing Time", description: "Players get far more matches than in a single-elimination knockout."},
    {title: "Perfect for Clubs", description: "Ideal for padel clubs, tennis clubs, pickleball groups and social leagues."},
    {title: "Simple Scheduling", description: "Fixtures are easy to follow and split cleanly across multiple courts."},
];

const VS_ROWS = [
    {feature: "Every player gets multiple matches", rr: "Yes", ko: "No"},
    {feature: "Best for fair rankings", rr: "Yes", ko: "No"},
    {feature: "Fastest format", rr: "No", ko: "Yes"},
    {feature: "Good for leagues", rr: "Yes", ko: "No"},
    {feature: "Good for finals", rr: "Sometimes", ko: "Yes"},
];

const FAQS = [
    {
        q: 'How many rounds are in a round robin tournament?',
        a: <>With an even number of participants a single round robin has n−1 rounds. With an odd number, one participant receives a bye each round and there are still n rounds played in total.</>,
    },
    {
        q: 'How many matches are in a round robin tournament?',
        a: <>A single round robin has n × (n − 1) ÷ 2 matches, where n is the number of participants. For example, 8 players produce 28 matches.</>,
    },
    {
        q: 'Can I use this for padel, tennis or pickleball?',
        a: <>Yes. The generator works for any sport where players or teams each need to face one another — padel, tennis, pickleball, football, basketball, baseball, cricket, rugby and esports.</>,
    },
    {
        q: 'Can ScorePal manage the full tournament?',
        a: <>Absolutely. Beyond generating fixtures, ScorePal handles registrations, live scores, standings, rankings and player communication from one platform.</>,
    },
];

function RouteComponent() {
    const [playersText, setPlayersText] = useState("");
    const [courts, setCourts] = useState("2");
    const [schedule, setSchedule] = useState<Round[] | null>(null);
    const [summary, setSummary] = useState<{participants: number; matches: number; slots: number} | null>(null);
    const [error, setError] = useState<string | null>(null);

    const gen = useStagedGenerate(["Pairing every participant", "Building the round rotation", "Balancing court slots"]);

    // Same handoff the results panel uses — hoisted so the top CTA and MakeItLiveCta can't drift.
    const buildPayload = () => {
        const teams = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        return teams.length >= 2 ? {source: "round-robin-tournament-generator", format: "round_robin" as const, sport: "padel", teams} : null;
    };

    const generate = () => {
        const entrants = playersText
            .split("\n")
            .map((name) => name.trim())
            .filter(Boolean);

        if (entrants.length < 2) {
            setError("Please enter at least two players or teams.");
            setSchedule(null);
            setSummary(null);
            return;
        }

        const courtCount = Math.max(1, parseInt(courts, 10) || 1);
        const totalMatches = (entrants.length * (entrants.length - 1)) / 2;

        setError(null);
        setSchedule(null);
        setSummary(null);
        gen.run(() => {
            setSchedule(buildRoundRobin(entrants));
            setSummary({
                participants: entrants.length,
                matches: totalMatches,
                slots: Math.ceil(totalMatches / courtCount),
            });
        });
    };

    const faqSchema = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {"@type": "Question", "name": "How many rounds are in a round robin tournament?", "acceptedAnswer": {"@type": "Answer", "text": "With an even number of participants a single round robin has n-1 rounds. With an odd number, one participant receives a bye each round."}},
            {"@type": "Question", "name": "How many matches are in a round robin tournament?", "acceptedAnswer": {"@type": "Answer", "text": "A single round robin has n x (n - 1) / 2 matches, where n is the number of participants."}},
            {"@type": "Question", "name": "Can I use this for padel, tennis or pickleball?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. The generator works for any sport where players or teams each need to face one another."}},
            {"@type": "Question", "name": "Can ScorePal manage the full tournament?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal handles registrations, live scores, standings, rankings and player communication from one platform."}},
        ],
    }), []);

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Round Robin Tournament Generator (Free) | Create Fixtures in Seconds</title>
            <meta name="description" content="Generate round robin tournament schedules instantly. Create leagues and tournaments for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby and esports. Free online round robin generator." />
            <link rel="canonical" href={`${config.publicUrl}/round-robin-tournament-generator/`} />

            <JsonLd id={`page${Route.id}/faq-schema`} item={faqSchema} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "SoftwareApplication",
                        "name": "Round Robin Tournament Generator",
                        "description": "Generate round robin tournament schedules, fixtures and standings instantly for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby and esports with ScorePal.",
                        "url": "https://scorepal.co/round-robin-tournament-generator/",
                        "applicationCategory": "SportsApplication",
                        "operatingSystem": ["Web", "Android", "iOS"],
                        "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
                    },
                    {
                        "@type": "HowTo",
                        "name": "How to Generate a Round Robin Tournament",
                        "step": [
                            {"@type": "HowToStep", "text": "Add your players or teams"},
                            {"@type": "HowToStep", "text": "Generate the round robin fixtures"},
                            {"@type": "HowToStep", "text": "Run your event with live scores and standings"},
                        ],
                    },
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Round Robin Tournament Generator", item: "https://scorepal.co/round-robin-tournament-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free Tournament Tool
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Free Round Robin Tournament Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create round robin fixtures instantly for any sport. Generate schedules, standings and matchups in seconds.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <a href="#how-it-works" className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        How It Works
                                    </a>
                                </div>
                            </div>

                            {/* Interactive generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Generate Fixtures</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter your players or teams and create a full schedule.</p>

                                <label htmlFor="players" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players or teams</label>
                                <textarea
                                    id="players"
                                    value={playersText}
                                    onChange={(e) => setPlayersText(e.target.value)}
                                    placeholder={"Enter one player or team per line\nPlayer 1\nPlayer 2\nPlayer 3\nPlayer 4"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <label htmlFor="courts" className="block font-inter font-semibold text-[#1a1a2e] mt-4 mb-2">Number of courts</label>
                                <input
                                    id="courts"
                                    type="number"
                                    min={1}
                                    value={courts}
                                    onChange={(e) => setCourts(e.target.value)}
                                    className="w-full rounded-xl border border-[#e5e7eb] p-3 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={gen.running}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {gen.running ? "Generating…" : "Create Schedule"}
                                </button>

                                <GeneratorProgress gen={gen} />

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.participants}</strong> participants ·{" "}
                                        <strong>{summary.matches}</strong> matches · approx.{" "}
                                        <strong>{summary.slots}</strong> court slots
                                    </p>
                                )}

                                {schedule && <GeneratorTopCta buildPayload={buildPayload} />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results */}
            {schedule && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-2">
                            Your Round Robin Schedule
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 mb-8">Every participant plays each other once.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {schedule.map((round) => (
                                <div key={round.label} className="bg-[#F6F7FB] border border-[#e5e7eb] rounded-2xl p-5">
                                    <div className="font-bebas text-xl text-[#082F8A] font-bold mb-3">{round.label}</div>
                                    <ul className="space-y-2">
                                        {round.matches.length === 0 && (
                                            <li className="font-inter text-sm text-[#1a1a2e]/50">Bye round</li>
                                        )}
                                        {round.matches.map((match, i) => (
                                            <li key={i} className="bg-white border border-[#e5e7eb] rounded-lg px-3 py-2 font-inter text-sm text-[#1a1a2e]">
                                                <span className="text-[#1a1a2e]/50 mr-2">Match {i + 1}</span>{match}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        <MakeItLiveCta formatLabel="round-robin" buildPayload={buildPayload} />
                    </div>
                </section>
            )}

            {/* What is a round robin / steps */}
            <section id="how-it-works" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is a Round Robin Tournament?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        A round robin tournament is a format where every player or team plays every other participant. It is
                        one of the fairest structures because final rankings come from overall performance across all matches,
                        not a single elimination result.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">
                                    {index + 1}
                                </div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Match calculator */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        Round Robin Match Calculator
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">The number of matches in a single round robin tournament is:</p>

                    <div className="my-6 inline-block rounded-2xl bg-[#EDEEF4] px-8 py-5 font-bebas text-3xl font-bold text-[#082F8A] tracking-wide">
                        n × (n − 1) ÷ 2
                    </div>
                    <p className="font-inter text-[#1a1a2e]/80 text-base mb-2">Where <strong>n</strong> is the number of players or teams.</p>

                    <div className="overflow-x-auto mt-6">
                        <div className="min-w-[420px] border border-[#d9dce7] rounded-2xl overflow-hidden bg-white shadow-sm max-w-xl">
                            <div className="grid grid-cols-2 bg-[#F6F7FB] font-bebas text-lg text-[#1a1a2e]">
                                <div className="p-4 border-r border-[#d9dce7]">Participants</div>
                                <div className="p-4">Total Matches</div>
                            </div>
                            {MATCH_TABLE.map((row, i) => (
                                <div key={row.n} className={`grid grid-cols-2 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-4 border-r border-[#d9dce7]">{row.n}</div>
                                    <div className="p-4 font-medium">{row.matches}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why round robin */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        Why Use a Round Robin Format?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {WHY_ITEMS.map((item) => (
                            <div key={item.title} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-3">
                                    <CheckBadgeIcon size={24} />
                                    <h3 className="font-inter text-[#082F8A] font-semibold text-base">{item.title}</h3>
                                </div>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* RR vs Knockout */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        Round Robin vs Knockout Tournament
                    </h2>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">Round Robin</div>
                                <div className="p-5 text-[#1a1a2e]">Knockout</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.rr}</div>
                                    <div className="p-5 text-[#1a1a2e]/80">{row.ko}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your Tournament With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-10">
                        Generate fixtures, manage registrations, publish live scores and track standings from one simple
                        tournament management platform.
                    </p>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Round Robin Tournament FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
